import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PenTool, CheckCircle, XCircle } from 'lucide-react';

const SENTENCES = [
  { wrong: 'She don\'t like apples.', correct: 'She doesn\'t like apples.', error: 'don\'t', fix: 'doesn\'t', rule: 'Use "doesn\'t" with he/she/it' },
  { wrong: 'The childrens are playing.', correct: 'The children are playing.', error: 'childrens', fix: 'children', rule: '"Children" is already plural' },
  { wrong: 'He runned to the store.', correct: 'He ran to the store.', error: 'runned', fix: 'ran', rule: '"Run" is an irregular verb (ran, not runned)' },
  { wrong: 'Me and him went to school.', correct: 'He and I went to school.', error: 'Me and him', fix: 'He and I', rule: 'Use subject pronouns (I, He) as subjects' },
  { wrong: 'Their going to the park.', correct: 'They\'re going to the park.', error: 'Their', fix: 'They\'re', rule: '"They\'re" = They are, "Their" = belonging to them' },
  { wrong: 'The dog wagged it\'s tail.', correct: 'The dog wagged its tail.', error: 'it\'s', fix: 'its', rule: '"Its" = possessive, "It\'s" = It is' },
  { wrong: 'She is more taller than me.', correct: 'She is taller than me.', error: 'more taller', fix: 'taller', rule: 'Don\'t use "more" with "-er" comparatives' },
  { wrong: 'I have went to Delhi.', correct: 'I have gone to Delhi.', error: 'went', fix: 'gone', rule: 'Use past participle with "have" (gone, not went)' },
  { wrong: 'Each of the boys have a pen.', correct: 'Each of the boys has a pen.', error: 'have', fix: 'has', rule: '"Each" takes a singular verb (has)' },
  { wrong: 'He gived me a book.', correct: 'He gave me a book.', error: 'gived', fix: 'gave', rule: '"Give" is irregular: give-gave-given' },
];

const SentenceFixer = () => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [userFix, setUserFix] = useState('');
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [completed, setCompleted] = useState(false);
  const total = 8;

  const current = SENTENCES[index];

  const checkFix = () => {
    const correct = userFix.toLowerCase().trim() === current.fix.toLowerCase();
    if (correct) { setScore(s => s + 15); setFeedback({ correct: true, msg: `Correct! ${current.rule}` }); }
    else setFeedback({ correct: false, msg: `The fix: "${current.fix}". ${current.rule}` });
    setTimeout(() => { if (index < total - 1) { setIndex(i => i+1); setUserFix(''); setFeedback(null); } else setCompleted(true); }, 2000);
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-10 shadow-2xl max-w-md w-full text-center border-2 border-slate-100">
          <div className="text-7xl mb-4">✏️</div>
          <h2 className="text-4xl font-black text-[#1E293B] mb-2">Grammar Pro!</h2>
          <p className="text-6xl font-black text-indigo-500 mb-2">{score}</p>
          <p className="text-xl text-[#64748B] mb-8">out of {total * 15} points</p>
          <div className="space-y-3">
            <button onClick={() => { setIndex(0); setScore(0); setCompleted(false); setUserFix(''); setFeedback(null); }} className="w-full py-4 bg-indigo-500 text-white rounded-2xl font-bold border-b-4 border-indigo-700 active:border-b-0 active:translate-y-1 transition-all">Play Again</button>
            <button onClick={() => navigate('/games')} className="w-full py-4 bg-slate-100 text-[#1E293B] rounded-2xl font-bold">Back to Brain Arena</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50" data-testid="sentence-fixer-game">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <div className="flex gap-3"><span className="bg-indigo-500 text-white px-4 py-2 rounded-2xl font-bold">{index+1}/{total}</span><span className="bg-[#4D96FF] text-white px-4 py-2 rounded-2xl font-bold">{score}pts</span></div>
        </div>
        <div className="bg-white rounded-3xl p-10 border-2 border-slate-100 shadow-xl text-center">
          <PenTool className="w-12 h-12 text-indigo-500 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-[#1E293B] mb-6">Fix the Error!</h2>
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mb-6">
            <p className="text-xl text-[#1E293B]">
              {current.wrong.split(current.error).map((part, i, arr) => (
                <React.Fragment key={i}>
                  {part}
                  {i < arr.length - 1 && <span className="bg-red-200 px-2 py-1 rounded-lg font-bold text-red-700 underline decoration-wavy">{current.error}</span>}
                </React.Fragment>
              ))}
            </p>
          </div>
          <p className="text-[#64748B] mb-3">Replace <strong className="text-red-500">"{current.error}"</strong> with:</p>
          <input type="text" value={userFix} onChange={(e) => setUserFix(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && userFix && checkFix()}
            className="w-full text-center text-xl font-bold p-4 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:border-indigo-500 outline-none mb-4" placeholder="Type the correction..." autoFocus data-testid="fix-input" />
          {feedback && <div className={`p-4 rounded-2xl font-bold mb-4 text-left ${feedback.correct ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>{feedback.correct ? <CheckCircle className="w-5 h-5 inline mr-2" /> : <XCircle className="w-5 h-5 inline mr-2" />}{feedback.msg}</div>}
          <button onClick={checkFix} disabled={!userFix} className="px-10 py-4 bg-indigo-500 text-white rounded-2xl font-bold border-b-4 border-indigo-700 active:border-b-0 active:translate-y-1 transition-all disabled:opacity-50" data-testid="check-fix-btn">Check Fix</button>
        </div>
      </div>
    </div>
  );
};

export default SentenceFixer;
